import React, { useEffect, useRef, useState } from 'react';
import {
	Autocomplete,
	Badge,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	Grid,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
	Clear as ClearIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	FilterList as FilterListIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
	IProviderPayableLinePaymentListFilters,
	IProviderAutocomplete,
	ProviderPayableLinePaymentTypeEnum,
} from 'features/salesOrder/types';
import { getProviderAutocomplete } from 'features/salesOrder/services';
import { CIVIL_DATE_PRESET_LABELS, CivilDatePreset, getDateRangeFromPreset } from 'features/common/helpers/datePresets';
import { PaymentsQuickDateBar } from '../PaymentsQuickDateBar';

const PAYMENT_TYPE_LABELS: Record<ProviderPayableLinePaymentTypeEnum, string> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'Tarjeta Corporativa',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'C2A',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'Transferencia Bancaria',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'Cheque',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'Efectivo',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'Otro',
};

const ALL_PAYMENT_TYPES = Object.values(ProviderPayableLinePaymentTypeEnum);
const ALL_MARKETS = ['UY', 'CO', 'PY', 'BO'];
/** Espera antes de propagar notas/observación al listado y disparar el request */
const FILTER_DEBOUNCE_MS = 500;

const toDateString = (d: Date | null): string | undefined => {
	if (!d) return undefined;
	return format(d, 'yyyy-MM-dd');
};

interface LocalFilters {
	providers: IProviderAutocomplete[];
	ticket: string;
	paymentTypes: ProviderPayableLinePaymentTypeEnum[];
	sellerMarkets: string[];
	notes: string;
	observation: string;
	dateFrom: Date | null;
	dateTo: Date | null;
	createdFrom: Date | null;
	createdTo: Date | null;
}

const defaultFilters: LocalFilters = {
	providers: [],
	ticket: '',
	paymentTypes: [],
	sellerMarkets: [],
	notes: '',
	observation: '',
	dateFrom: null,
	dateTo: null,
	createdFrom: null,
	createdTo: null,
};

interface DebouncedTextFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
}

const DebouncedTextField = ({ label, value, onChange }: DebouncedTextFieldProps): JSX.Element => {
	const [draft, setDraft] = useState(value);
	const debouncerRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		setDraft(value);
	}, [value]);

	useEffect(
		() => () => {
			if (debouncerRef.current) clearTimeout(debouncerRef.current);
		},
		[],
	);

	const handleChange = (next: string) => {
		setDraft(next);
		if (debouncerRef.current) clearTimeout(debouncerRef.current);
		debouncerRef.current = setTimeout(() => onChange(next.trim()), FILTER_DEBOUNCE_MS);
	};

	return (
		<TextField fullWidth size="small" label={label} value={draft} onChange={(e) => handleChange(e.target.value)} />
	);
};

interface Props {
	onFiltersChange: (filters: IProviderPayableLinePaymentListFilters) => void;
	onClearFilters: () => void;
	hideProviderFilter?: boolean;
	embedded?: boolean;
}

export const ProviderPayablesPaymentsFilters = ({
	onFiltersChange,
	onClearFilters,
	hideProviderFilter = false,
	embedded = false,
}: Props): JSX.Element => {
	const [filters, setFilters] = useState<LocalFilters>(defaultFilters);
	const [paymentDatePreset, setPaymentDatePreset] = useState<CivilDatePreset | ''>('');
	const [expanded, setExpanded] = useState(false);
	const [providerOptions, setProviderOptions] = useState<IProviderAutocomplete[]>([]);
	const [providerLoading, setProviderLoading] = useState(false);
	const [providerInput, setProviderInput] = useState('');

	const prevFiltersRef = useRef<string>('');
	const onFiltersChangeRef = useRef(onFiltersChange);

	useEffect(() => {
		onFiltersChangeRef.current = onFiltersChange;
	}, [onFiltersChange]);

	useEffect(() => {
		if (providerInput.length < 4) {
			setProviderOptions([]);
			return;
		}
		const timer = setTimeout(async () => {
			try {
				setProviderLoading(true);
				const res = await getProviderAutocomplete(providerInput);
				setProviderOptions(res.data);
			} catch {
				setProviderOptions([]);
			} finally {
				setProviderLoading(false);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [providerInput]);

	useEffect(() => {
		const toParent: IProviderPayableLinePaymentListFilters = {
			providerIds: filters.providers.length > 0 ? filters.providers.map((p) => String(p.id)) : undefined,
			ticket: filters.ticket || undefined,
			paymentTypes: filters.paymentTypes.length > 0 ? filters.paymentTypes : undefined,
			sellerMarkets: filters.sellerMarkets.length > 0 ? filters.sellerMarkets : undefined,
			notes: filters.notes || undefined,
			observation: filters.observation || undefined,
			dateFrom: toDateString(filters.dateFrom),
			dateTo: toDateString(filters.dateTo),
			createdFrom: toDateString(filters.createdFrom),
			createdTo: toDateString(filters.createdTo),
		};
		const asString = JSON.stringify(toParent);
		if (prevFiltersRef.current !== asString) {
			prevFiltersRef.current = asString;
			onFiltersChangeRef.current(toParent);
		}
	}, [filters]);

	const handleChange = <K extends keyof LocalFilters>(field: K, value: LocalFilters[K]) => {
		setFilters((prev) => ({ ...prev, [field]: value }));
	};

	const handlePaymentDateChange = (field: 'dateFrom' | 'dateTo', value: Date | null) => {
		setPaymentDatePreset('');
		handleChange(field, value);
	};

	const handlePaymentDatePresetToggle = (preset: CivilDatePreset) => {
		if (paymentDatePreset === preset) {
			setPaymentDatePreset('');
			setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }));
			return;
		}

		const { from, to } = getDateRangeFromPreset(preset);
		setPaymentDatePreset(preset);
		setFilters((prev) => ({
			...prev,
			dateFrom: from,
			dateTo: to,
		}));
	};

	const handleClear = () => {
		setFilters(defaultFilters);
		setPaymentDatePreset('');
		setProviderInput('');
		setExpanded(false);
		onClearFilters();
	};

	const activeCount = [
		!hideProviderFilter && filters.providers.length > 0,
		!!filters.ticket,
		filters.paymentTypes.length > 0,
		filters.sellerMarkets.length > 0,
		!!filters.notes,
		!!filters.observation,
		!!filters.dateFrom,
		!!filters.dateTo,
		!!filters.createdFrom,
		!!filters.createdTo,
	].filter(Boolean).length;

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<PaymentsQuickDateBar activePreset={paymentDatePreset} onPresetToggle={handlePaymentDatePresetToggle} />

			<Paper elevation={2} sx={{ p: 2, mb: 2, mt: embedded ? 0 : 3 }}>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
					<Box display="flex" alignItems="center" gap={1}>
						<Badge badgeContent={activeCount} color="primary">
							<FilterListIcon color="action" />
						</Badge>
						<Typography variant="subtitle1" fontWeight={500}>
							Filtros:
						</Typography>
					</Box>

					{activeCount > 0 && (
						<Box display="flex" flexWrap="wrap" gap={0.5} flex={1}>
							{!hideProviderFilter &&
								filters.providers.map((p) => (
									<Chip
										key={p.id}
										label={p.fantasyName}
										size="small"
										color="primary"
										variant="outlined"
										onDelete={() =>
											handleChange(
												'providers',
												filters.providers.filter((x) => x.id !== p.id),
											)
										}
									/>
								))}
							{filters.paymentTypes.map((t) => (
								<Chip
									key={t}
									label={`Tipo: ${PAYMENT_TYPE_LABELS[t]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										handleChange(
											'paymentTypes',
											filters.paymentTypes.filter((x) => x !== t),
										)
									}
								/>
							))}
							{filters.notes && (
								<Chip
									label={`Nota: ${filters.notes}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('notes', '')}
								/>
							)}
							{filters.observation && (
								<Chip
									label={`Obs.: ${filters.observation}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('observation', '')}
								/>
							)}
							{paymentDatePreset ? (
								<Chip
									label={`Pago: ${CIVIL_DATE_PRESET_LABELS[paymentDatePreset]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handlePaymentDatePresetToggle(paymentDatePreset)}
								/>
							) : (
								<>
									{filters.dateFrom && (
										<Chip
											label={`Pago desde: ${format(filters.dateFrom, 'dd/MM/yyyy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handlePaymentDateChange('dateFrom', null)}
										/>
									)}
									{filters.dateTo && (
										<Chip
											label={`Pago hasta: ${format(filters.dateTo, 'dd/MM/yyyy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handlePaymentDateChange('dateTo', null)}
										/>
									)}
								</>
							)}
							{filters.ticket && (
								<Chip
									label={`Ticket: ${filters.ticket}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('ticket', '')}
								/>
							)}
							{filters.sellerMarkets.map((m) => (
								<Chip
									key={m}
									label={`Mercado: ${m}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										handleChange(
											'sellerMarkets',
											filters.sellerMarkets.filter((x) => x !== m),
										)
									}
								/>
							))}
							{filters.createdFrom && (
								<Chip
									label={`Creación desde: ${format(filters.createdFrom, 'dd/MM/yyyy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('createdFrom', null)}
								/>
							)}
							{filters.createdTo && (
								<Chip
									label={`Creación hasta: ${format(filters.createdTo, 'dd/MM/yyyy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('createdTo', null)}
								/>
							)}
						</Box>
					)}

					<Box display="flex" gap={1}>
						<Tooltip title="Limpiar todos los filtros">
							<Button size="small" startIcon={<ClearIcon />} onClick={handleClear} variant="outlined" color="error">
								Limpiar
							</Button>
						</Tooltip>
						<Tooltip title={expanded ? 'Ocultar filtros avanzados' : 'Mostrar más filtros'}>
							<Button
								size="small"
								endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setExpanded((prev) => !prev)}
								variant="outlined"
								color="primary"
							>
								{expanded ? 'Menos filtros' : 'Más filtros'}
							</Button>
						</Tooltip>
					</Box>
				</Box>

				<Grid container spacing={1.5}>
					{!hideProviderFilter && (
						<Grid item xs={12} sm={6} md={2}>
							<Autocomplete
								multiple
								options={providerOptions}
								value={filters.providers}
								inputValue={providerInput}
								loading={providerLoading}
								getOptionLabel={(o) => `${o.fantasyName} (${o.businessName})`}
								isOptionEqualToValue={(o, v) => o.id === v.id}
								onInputChange={(_, val) => setProviderInput(val)}
								onChange={(_, val) => handleChange('providers', val)}
								noOptionsText={providerInput.length < 4 ? 'Escribí al menos 4 caracteres' : 'Sin resultados'}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip {...getTagProps({ index })} key={option.id} label={option.fantasyName} size="small" />
									))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Proveedor"
										size="small"
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{providerLoading && <CircularProgress size={16} />}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>
						</Grid>
					)}

					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							options={ALL_PAYMENT_TYPES}
							value={filters.paymentTypes}
							getOptionLabel={(o) => PAYMENT_TYPE_LABELS[o]}
							onChange={(_, val) => handleChange('paymentTypes', val)}
							disableCloseOnSelect
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option} label={PAYMENT_TYPE_LABELS[option]} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Tipo de pago" size="small" />}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DebouncedTextField label="Notas" value={filters.notes} onChange={(val) => handleChange('notes', val)} />
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DebouncedTextField
							label="Observación"
							value={filters.observation}
							onChange={(val) => handleChange('observation', val)}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Pago desde"
							value={filters.dateFrom}
							onChange={(d) => handlePaymentDateChange('dateFrom', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Pago hasta"
							value={filters.dateTo}
							onChange={(d) => handlePaymentDateChange('dateTo', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
				</Grid>

				<Collapse in={expanded}>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={1.5}>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Ticket / Localizador"
								value={filters.ticket}
								onChange={(e) => handleChange('ticket', e.target.value)}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<Autocomplete
								multiple
								options={ALL_MARKETS}
								value={filters.sellerMarkets}
								getOptionLabel={(o) => o}
								onChange={(_, val) => handleChange('sellerMarkets', val)}
								disableCloseOnSelect
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip {...getTagProps({ index })} key={option} label={option} size="small" />
									))
								}
								renderInput={(params) => <TextField {...params} label="Mercado" size="small" placeholder="UY, CO…" />}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<DatePicker
								label="Creación desde"
								value={filters.createdFrom}
								onChange={(d) => handleChange('createdFrom', d)}
								inputFormat="dd/MM/yyyy"
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<DatePicker
								label="Creación hasta"
								value={filters.createdTo}
								onChange={(d) => handleChange('createdTo', d)}
								inputFormat="dd/MM/yyyy"
								renderInput={(params) => <TextField {...params} size="small" fullWidth />}
							/>
						</Grid>
					</Grid>
				</Collapse>
			</Paper>
		</LocalizationProvider>
	);
};
